import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityFileTypeEnum,
  ActivityTypeEnum,
  BookingItemStatusEnum,
  OrganizationInterface,
} from '@sport-activities/sdk';
import { differenceInMinutes } from 'date-fns';
import { ClipboardService } from 'ngx-clipboard';

import { BookingTypeEnum } from '~/attendee/enums/booking-type.enum';
import { Subscription } from '~/attendee/model/subscription.model';
import { ReviewsService } from '~/attendee/service/reviews.service';
import { BookingService } from '~/checkout/services/booking/booking.service';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { formatIsoDuration } from '~/core/helpers/duration-formatter/duration-formatter.helper';
import { getRefundTimeLimit } from '~/core/helpers/refund-time-limit/refund-time-limit.helper';
import { ActivityService } from '~/core/services/activity/activity.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { OrganizationService } from '~/core/services/organization/organization.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { MessagingService } from '~/messaging/services/messaging.service';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import { Subscription as RxjsSubscription } from 'rxjs';
import { WindowService } from '~/core/services/window/window.service';
import { WalletPassResponse } from '~/checkout/components/pages/confirmation/confirmation.component';
import { appConfig } from '~/app.config';

export const SUBSCRIPTION_LOADER_KEY = 'loadSubscription';

@Component({
  selector: 'app-attendee-booking-detail',
  templateUrl: './booking-detail.component.html',
  styleUrls: ['./booking-detail.component.scss'],
})
export class AttendeeBookingDetailComponent implements OnInit {
  @Input() public canRefund = null;
  @Input() public isPastTab: boolean;
  @Input() public subscription: Subscription;
  @Input() public type: BookingTypeEnum;
  @Input() public isOpened: boolean;
  @Input() public bookingId?: string;

  @Output() public readonly cancelled = new EventEmitter<void>();

  @ViewChild('confirmedModal') public confirmedModal: ModalComponent;
  @ViewChild('refundModal') public refundModal: ModalComponent;

  private readonly subscriptions = new RxjsSubscription();
  public readonly dateLocale: Locale;
  public readonly icon = VtmnPlayIconTypeEnum;
  public readonly subscriptionLoaderKey = SUBSCRIPTION_LOADER_KEY;
    public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public isLoading = false;

  public hasLiveButtons = false;
  public hasLiveLink = true;
  public hasNoteForAttendees = true;
  public liveEventNoteForAttendees: string;
  public liveEventUrlToDisplay: string;
  public organization: OrganizationInterface;
  public invoiceUrl: string;
  public isMobileDevice: boolean;
  public bookingItemId: string;

  constructor(
    private readonly bookingService: BookingService,
    private readonly paramethorService: ParamethorService,
    private readonly localeService: LocaleService,
    private readonly translateService: TranslateService,
    private readonly _clipboardService: ClipboardService,
    private readonly messagingService: MessagingService,
    private readonly organizationService: OrganizationService,
    private readonly redirectionService: RedirectionService,
    private readonly reviewsService: ReviewsService,
    private readonly activityService: ActivityService,
    private readonly loaderService: LoaderService,
    private readonly matchMediaService: MatchMediaService,
    private readonly windowService: WindowService
  ) {
    this.dateLocale = this.localeService.dateLocale;
  }

  public ngOnInit(): void {
    if (this.subscription?.activity?.isEvent) {
      this.subscriptions.add(
        this.bookingService.getBookingItemId(this.bookingId).subscribe({
          next: (data) => (this.bookingItemId = data.items?.[0]?.identifier),
        })
      );
    }

    const timeDifferenceInMinutes = differenceInMinutes(
      new Date(this.subscription?.timeslot?.startDate),
      new Date(),
      { roundingMethod: 'round' }
    );

    if (timeDifferenceInMinutes > 30) {
      this.liveEventUrlToDisplay = this.translateService.instant(
        'attendee.bookings.live.link.before.placeholder'
      );
      this.liveEventNoteForAttendees = this.translateService.instant(
        'attendee.bookings.live.link.before.placeholder'
      );
    }

    if (timeDifferenceInMinutes > 0 && timeDifferenceInMinutes <= 30) {
      const regrexToTestValidUrl = /^[a-z0-9]+:\/\//;

      this.hasLiveLink = !!this.subscription?.timeslot.liveUrl;
      this.liveEventUrlToDisplay =
        this.subscription?.timeslot?.liveUrl ||
        this.translateService.instant('attendee.bookings.live.link.not_set');

      if (
        this.subscription?.timeslot?.liveUrl &&
        !regrexToTestValidUrl.test(this.liveEventUrlToDisplay)
      ) {
        this.liveEventUrlToDisplay = `https://${this.liveEventUrlToDisplay}`;
      }

      this.hasNoteForAttendees =
        !!this.subscription?.timeslot?.noteForAttendees;
      this.liveEventNoteForAttendees =
        this.subscription?.timeslot?.noteForAttendees ||
        this.translateService.instant(
          'attendee.bookings.live.access.no_password_needed'
        );

      this.hasLiveButtons = true;
    }

    if (timeDifferenceInMinutes <= 0) {
      this.liveEventUrlToDisplay = this.translateService.instant(
        'attendee.bookings.live.link.after.placeholder'
      );
      this.liveEventNoteForAttendees = this.translateService.instant(
        'attendee.bookings.live.access.after.placeholder'
      );
    }

    if (this.haveOrganization) {
      this.organizationService
        .getOrganizationById(
          this.subscription?.activity?.organizationIdentifier
        )
        .subscribe((organization) => (this.organization = organization));
    }

    this.bookingService
      .downloadInvoice(this.subscription?.item?.identifier)
      .subscribe((item) => {
        this.invoiceUrl = item?.url;
      });

    this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
      this.isMobileDevice = isMobileDevice;
    });
  }

  public get codeIcon() {
    return this.subscription?.codesAttachement?.type === 'barcode'
      ? this.icon.BarCodeScan
      : this.icon.QrCode;
  }

  private get isTimeslotFinished(): boolean {
    return (
      differenceInMinutes(
        new Date(this.subscription?.timeslot?.endDate),
        new Date(),
        { roundingMethod: 'round' }
      ) <= 0
    );
  }

  public get displayLiveConnectionDetails(): boolean {
    return (
      this.subscription?.activity?.isLive &&
      this.type !== BookingTypeEnum.CANCELED
    );
  }

  public get getRefundTime(): string {
    return getRefundTimeLimit(
      this.subscription?.activity,
      appConfig.refundLimit
    );
  }

  public get haveOrganization(): boolean {
    return !!this.subscription?.activity?.organizationIdentifier;
  }

  public get isReviewable(): boolean {
    return (
      this.isTimeslotFinished &&
      this.subscription?.item?.status === ('COMPLETED' as BookingItemStatusEnum)
    );
  }

  public get noRefundMessage(): string {
    return this.translateService.instant(
      'bookings-list.modal.no_refund.message',
      {
        refundTimeLimit: formatIsoDuration(
          getRefundTimeLimit(
            this.subscription?.activity,
            appConfig.refundLimit
          ),
          this.localeService.dateLocale,
          true
        ),
      }
    );
  }

  public get qrCodeTranslation(): string {
    // if barcode
    if (this.subscription?.codesAttachement?.type === 'barcode') {
      if (this.subscription?.item?.attendees?.length > 1) {
        return this.translateService.instant('booking.offer.barcodes');
      }
      return this.translateService.instant('booking.offer.barcode');
    }
    // if qr-code
    if (this.subscription?.item?.attendees?.length > 1) {
      return this.translateService.instant('booking.offer.qr-codes');
    }
    return this.translateService.instant('booking.offer.qr-code');
  }

  public get path(): string {
    return this.subscription?.activity?.isLive
      ? '/attendee.show-live-activity'
      : '/attendee.show-activity';
  }

  public get picture(): string {
    return (
      this.subscription?.activity?.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || this.subscription?.activity?.picture
    );
  }

  public get codeSubscription(): boolean {
    return (
      this.subscription?.codesAttachement &&
      this.subscription.codesAttachement.url !== ''
    );
  }

  public get url(): string {
    return this.subscription?.codesAttachement?.url;
  }

  public get refundEnabled(): boolean {
    return this.type === BookingTypeEnum.UPCOMING;
  }

  public get reviewPageUrl(): string {
    return this.reviewsService.getReviewsPageUrl(
      this.subscription?.activity?.sku.toString(),
      this.subscription?.activity?.organizationIdentifier,
      this.subscription?.timeslot?.identifier
    );
  }

  public get isTimezoneSelectionEnabled(): boolean {
    return this.paramethorService.isTrue(ParamethorNameEnum.TimezoneSelection);
  }

  public copyText(): void {
    this._clipboardService.copy(this.liveEventNoteForAttendees);
  }

  public hideConfirmationModal(): void {
    this.cancelled.emit();
  }

  public isLive(type: ActivityTypeEnum): boolean {
    return type === 'LIVE';
  }

  public refund(): void {
    this.isLoading = this.loaderService.isLoading(this.subscriptionLoaderKey);
    this.bookingService
      .refundItem(this.subscription?.item?.identifier)
      .subscribe(() => {
        this.refundModal.closeModal();
        this.confirmedModal.show();
        this.loaderService.stop(this.subscriptionLoaderKey);
      });
  }

  public storeMessagingData(): void {
    this.activityService
      .getActivityFounder(String(this.subscription?.activity?.sku))
      .subscribe((founder) => {
        const recipient = this.messagingService.buildRecipientFromOrganization({
          email: founder.email,
          name: founder.name,
          firstname: founder.firstname,
          id: founder.id,
        });
        const activityData = this.messagingService.buildActivityData(
          this.subscription?.activity,
          this.subscription?.timeslot
        );
        this.messagingService.storeRecipient(recipient);
        this.messagingService.storeActivityData(activityData);
        this.messagingService.isRecipientUpdated.next(true);
        this.redirectionService.redirect('/attendee.messages.inbox');
      });
  }

  public downloadWalletPass() {
    if (this.matchMediaService.isAppleDevice()) {
      this.bookingService.getAppleWalletPass(this.bookingItemId).subscribe({
        next: (data: Blob) => {
          const url = URL.createObjectURL(data);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = 'wallet-pass.pkpass';
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          console.error('Error for wallet download');
        },
      });
    } else if (this.matchMediaService.isAndroidDevice()) {
      this.bookingService.getGoogleWalletPass(this.bookingItemId).subscribe({
        next: (data: WalletPassResponse) => {
          this.windowService.openNewTab(data.url);
        },
        error: () => {
          console.error('Error for wallet download');
        },
      });
    }
  }
}
